import { Link } from "react-router-dom";
import { ArrowRight, MessagesSquare, ClipboardCheck, LineChart, Repeat, Users, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import CaseStudies from "@/components/home/CaseStudies";

const capabilities = [
  {
    icon: ClipboardCheck,
    title: "Interactive assessments",
    body:
      "Readers work through your own diagnostic frameworks and receive a personalised report — the same insight you would give in a paid session, delivered at scale.",
  },
  {
    icon: MessagesSquare,
    title: "An AI that speaks in your voice",
    body:
      "Trained exclusively on your manuscript and materials, with citations back to your chapters. It answers as you would, and never beyond what you wrote.",
  },
  {
    icon: Users,
    title: "Reader-to-client pathways",
    body:
      "Every report ends with a considered next step: a programme, a call, a workshop. Interest becomes a conversation instead of evaporating.",
  },
  {
    icon: LineChart,
    title: "Insight you can act on",
    body:
      "See which chapters land, which problems readers report most, and who is closest to buying. Your book becomes a research instrument.",
  },
  {
    icon: Repeat,
    title: "Revenue beyond launch week",
    body:
      "Subscriptions, cohorts and premium tiers keep earning long after the launch spike has passed — from a book you have already written.",
  },
  {
    icon: ShieldCheck,
    title: "Built and maintained for you",
    body:
      "We handle the build, the AI tuning and the upkeep. You approve the substance and keep full ownership of your content.",
  },
];

const AuthorityEngine = () => {
  return (
    <div className="min-h-screen">
      <Navbar />

      <PageHero
        eyebrow="Stage 03 · For published authors"
        title={
          <>
            Extend your published book into a{" "}
            <span className="text-gold-gradient">living platform.</span>
          </>
        }
        intro="A printed book gives every reader the same experience and then goes quiet. Extend gives each reader their own — a diagnostic, a personalised report, and a clear route into working with you. Same expertise, working continuously."
      >
        <Button asChild size="lg" className="bg-gold text-gold-foreground hover:bg-gold-light font-semibold px-8">
          <a
            href="https://meetings.series-a.co/meetings/series-a/authorpro"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2"
          >
            Book a call about Extend
            <ArrowRight className="h-4 w-4" />
          </a>
        </Button>
        <Button asChild size="lg" variant="outline" className="px-8">
          <Link to="/demo">See It in Action</Link>
        </Button>
        <Button asChild size="lg" variant="ghost" className="px-6">
          <Link to="/first-draft">Draft not reviewed yet?</Link>
        </Button>
      </PageHero>

      {/* Shelf vs engine */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">The shift</p>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1] mb-5">
              A book on a shelf, or a book at work.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Nothing about your expertise changes. What changes is what happens after someone reads
              chapter one.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="card-noir p-8 md:p-10">
              <p className="eyebrow mb-6 !text-muted-foreground">The printed book alone</p>
              <ul className="space-y-4">
                {[
                  "One fixed experience for every reader",
                  "You never learn who read it, or what they needed",
                  "Royalties arrive once, then taper away",
                  "Interested readers have no obvious next step",
                ].map((item) => (
                  <li key={item} className="flex gap-3 text-[15px] text-muted-foreground">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-muted-foreground" aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="card-noir p-8 md:p-10 relative overflow-hidden">
              <span className="absolute inset-x-0 top-0 h-px gold-hairline" aria-hidden />
              <p className="eyebrow mb-6">With Extend</p>
              <ul className="space-y-4">
                {[
                  "Every reader receives a personalised path",
                  "You see who engaged, what they struggle with, and who is ready",
                  "Recurring revenue from subscriptions and programmes",
                  "A clear, considered invitation to work with you",
                ].map((item) => (
                  <li key={item} className="flex gap-3 text-[15px] text-foreground/90">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gold" aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 md:py-28 bg-card border-y border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">What we build</p>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1]">
              The platform your book has been missing.
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {capabilities.map(({ icon: Icon, title, body }) => (
              <article key={title} className="card-noir-hover p-7">
                <span className="mb-6 flex h-11 w-11 items-center justify-center rounded-sm border border-border bg-background text-gold">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="text-xl font-semibold mb-3">{title}</h3>
                <p className="text-[15px] text-muted-foreground leading-relaxed">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Build process */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">How we build it</p>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1]">
              From manuscript to live platform.
            </h2>
          </div>

          <div className="grid gap-px bg-border md:grid-cols-2 lg:grid-cols-4 overflow-hidden rounded-lg border border-border">
            {[
              { step: "01", title: "Hand over your book", desc: "We ingest your manuscript, frameworks, worksheets and any existing programme material." },
              { step: "02", title: "Design the journey", desc: "We map the assessment, the report logic, and the offer each reader is invited into." },
              { step: "03", title: "Build and tune", desc: "Your platform is built and the AI is grounded in your content, then reviewed with you." },
              { step: "04", title: "Launch and grow", desc: "You go live with analytics, ongoing support, and a pipeline you can actually read." },
            ].map((s) => (
              <div key={s.step} className="bg-background p-8">
                <span className="font-serif italic text-gold text-lg">{s.step}</span>
                <h3 className="text-lg font-semibold mt-4 mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="border-t border-border">
        <CaseStudies />
      </div>

      {/* Closing call to action */}
      <section aria-labelledby="extend-cta-heading" className="relative overflow-hidden border-y border-border bg-card">
        <div className="absolute inset-x-0 top-0 h-px gold-hairline" aria-hidden />
        <div className="container mx-auto px-4 py-20 md:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">Next step</p>
            </div>
            <h2 id="extend-cta-heading" className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1] mb-6">
              Talk to us about extending your book.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-9 max-w-2xl">
              Bring your published book. In one conversation we will look at what an assessment,
              a personalised report and a reader-to-client pathway could look like for it.
            </p>
            <Button asChild size="lg" className="bg-gold text-gold-foreground hover:bg-gold-light font-semibold px-8 w-full sm:w-auto">
              <a
                href="https://meetings.series-a.co/meetings/series-a/authorpro"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2"
              >
                Book a call about Extend
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AuthorityEngine;
